import { create } from '@fhs/zustand';

import type { CommittedOrder } from '../../payment/payment-types';

type PaymentFlowState =
  | 'idle'
  | 'encrypting'
  | 'insertRequested'
  | 'serviceModeCheck'
  | 'validatingCart'
  | 'committing'
  | 'confirming'
  | 'vaulting'
  | 'success'
  | 'error';

export interface PaymentErrorInfo {
  code: string;
  message: string;
  canRetry: boolean;
}

export interface PaymentRetryInfo {
  canRetry: boolean;
  retryFromStep: string;
  errorCode: string;
  errorMessage: string;
  failedStep?: string;
}

export interface PaymentFlowStore {
  currentState: PaymentFlowState;
  retryInfo: PaymentRetryInfo | null;
  /** True while retrying after a commit failure — suppresses ambiguous "confirming order" UX. */
  isCommitRetry: boolean;
  /** True after the network drops during an in-flight place-order attempt. */
  isOfflineDuringCommit: boolean;
  rbiOrderId: string | null;
  pricedOrder: CommittedOrder | null;
  setRbiOrderId: (rbiOrderId: string | null) => void;
  setCurrentState: (state: PaymentFlowState) => void;
  setRetryInfo: (retryInfo: PaymentRetryInfo | null) => void;
  setIsCommitRetry: (isCommitRetry: boolean) => void;
  setIsOfflineDuringCommit: (isOfflineDuringCommit: boolean) => void;
  /** Promote checkout into the offline confirming UX as soon as connectivity drops. */
  enterOfflineConfirming: () => void;
  setPricedOrder: (order: CommittedOrder | null) => void;
  resetPaymentFlowStore: () => void;
}

const initialState = {
  currentState: 'idle' as PaymentFlowState,
  retryInfo: null,
  isCommitRetry: false,
  isOfflineDuringCommit: false,
  rbiOrderId: null,
  pricedOrder: null as CommittedOrder | null,
};

const ACTIVE_PIPELINE_STATES = new Set<PaymentFlowState>([
  'encrypting',
  'validatingCart',
  'serviceModeCheck',
  'committing',
  'confirming',
  'vaulting',
  'insertRequested',
]);

export const usePaymentFlowStore = create<PaymentFlowStore>((set, get) => ({
  ...initialState,
  setCurrentState: state => {
    if (state === 'error' || state === 'success' || state === 'idle') {
      set({ currentState: state, isOfflineDuringCommit: false });
      return;
    }
    set({ currentState: state });
  },
  setRetryInfo: retryInfo => set({ retryInfo }),
  setIsCommitRetry: isCommitRetry => set({ isCommitRetry }),
  setIsOfflineDuringCommit: isOfflineDuringCommit => set({ isOfflineDuringCommit }),
  enterOfflineConfirming: () => {
    const { currentState, isCommitRetry } = get();
    const shouldPromoteState =
      !isCommitRetry && ACTIVE_PIPELINE_STATES.has(currentState);

    set({
      isOfflineDuringCommit: true,
      ...(shouldPromoteState ? { currentState: 'confirming' as PaymentFlowState } : {}),
    });
  },
  setRbiOrderId: rbiOrderId => set({ rbiOrderId }),
  setPricedOrder: order => set({ pricedOrder: order }),
  // Reset to initial state
  resetPaymentFlowStore: () => set({ ...initialState, rbiOrderId: get().rbiOrderId }),
}));
